import { Controller } from 'react-hook-form';
import { FormControlLabel, Stack, Switch, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { FormDialog } from '../../../shared/components/FormDialog';
import { useCrudFormDialog } from '../../../shared/api/useCrudFormDialog';
import { clientProjectInputSchema, type ClientProjectFormValues } from './schemas';
import type { ClientProjectDto, ClientProjectInput } from './types';

export interface ClientProjectFormDialogProps {
  open: boolean;
  project: ClientProjectDto | null;
  hooks: {
    useCreate: () => UseMutationResult<ClientProjectDto, Error, ClientProjectInput>;
    useUpdate: () => UseMutationResult<
      ClientProjectDto,
      Error,
      { id: number; input: ClientProjectInput }
    >;
  };
  onClose: () => void;
}

const DEFAULT_VALUES: ClientProjectFormValues = {
  nome: '',
  sigla: '',
  ativo: true,
};

export function ClientProjectFormDialog({
  open,
  project,
  hooks,
  onClose,
}: ClientProjectFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    ClientProjectFormValues,
    ClientProjectDto,
    ClientProjectInput,
    number
  >({
    open,
    entity: project,
    schema: clientProjectInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (p) => ({ nome: p.nome, sigla: p.sigla, ativo: p.ativo }),
    getId: (p) => p.id,
    hooks,
    messages: {
      created: t('features.clientes.projetos.toasts.created'),
      updated: t('features.clientes.projetos.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.clientes.projetos.editTitle')
          : t('features.clientes.projetos.createTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <TextField
          label={t('features.clientes.projetos.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />
        <TextField
          label={t('features.clientes.projetos.fields.sigla')}
          fullWidth
          inputProps={{ maxLength: 10, style: { textTransform: 'uppercase' } }}
          error={Boolean(form.formState.errors.sigla)}
          helperText={
            form.formState.errors.sigla?.message ??
            t('features.clientes.projetos.fields.siglaHelper')
          }
          {...form.register('sigla')}
        />
        <Controller
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(_, c) => field.onChange(c)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.clientes.projetos.fields.ativo')}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}
